'use client';

import { type FC, memo, useMemo } from 'react';

import { Category, type GameV1 } from '@/api/games';
import { useUserWageringBonuses } from '@/hooks/bonuses';
import { Badge } from '@/modules/game/components/Badge';

import { ScBadges, ScBottomBadges } from './styled';

interface IBadgesProps extends Pick<GameV1, 'listCollections'> {
  isJackpot: boolean;
  isTournament: boolean;
  isHot?: boolean;
  isCold?: boolean;
  rtp?: string;
}

export const Badges: FC<IBadgesProps> = memo(
  ({
    isJackpot,
    isTournament,
    rtp,
    isHot,
    isCold,
    listCollections: categories,
  }) => {
    const { hasWageringBonus } = useUserWageringBonuses();

    const isWagering =
      categories.includes(Category.WAGERING) && hasWageringBonus;
    const isExclusive = categories.includes(Category.EXCLUSIVE);
    const isInstant = categories.includes(Category.CASUAL);
    const isNew = categories.includes(Category.NEW);
    const isTop = categories.join(',').includes(Category.TOP);

    const isAnyBadges =
      isWagering ||
      isExclusive ||
      isTop ||
      isJackpot ||
      isNew ||
      isTournament ||
      isInstant ||
      isHot ||
      isCold;

    const bottomBadges = [
      isHot && (
        <Badge
          className="hot-badge bottom-badge"
          content={`RTP: ${rtp}`}
          key="hot"
          type="hot"
        />
      ),
      isCold && (
        <Badge
          className="cold-badge bottom-badge"
          content={`RTP: ${rtp}`}
          key="cold"
          type="cold"
        />
      ),
    ];

    const badges = useMemo(() => {
      const isShortBadgesPack = isExclusive || isHot || isCold;

      const possibleBadges = isShortBadgesPack
        ? [
            isExclusive && <Badge key="exclusive" type="exclusive" />,
            isTournament && <Badge key="tournament" type="tournament" />,
            isWagering && <Badge key="wagering" type="wagering" />,
          ]
        : [
            isTournament && <Badge key="tournament" type="tournament" />,
            isWagering && <Badge key="wagering" type="wagering" />,
            isJackpot && <Badge key="jackpot" type="jackpot" />,
            isTop && <Badge key="top" type="top" />,
            isNew && <Badge className="new" key="new" type="new" />,
            isInstant && <Badge key="instant" type="instant" />,
          ];
      return possibleBadges.filter((b): b is JSX.Element => !!b).slice(0, 2);
    }, [
      isWagering,
      isExclusive,
      isTop,
      isJackpot,
      isNew,
      isTournament,
      isInstant,
      isHot,
      isCold,
    ]);

    if (!isAnyBadges) return null;

    if (badges.length === 0 && bottomBadges.length === 0) return null;

    return (
      <>
        <ScBadges>{badges}</ScBadges>
        <ScBottomBadges>{bottomBadges}</ScBottomBadges>
      </>
    );
  }
);
